import React from 'react'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Badge variant style
   */
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info'
  /**
   * Badge size
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Badge shape
   */
  shape?: 'rounded' | 'pill' | 'square'
  /**
   * Badge content
   */
  children: React.ReactNode
  /**
   * Icon to display before text
   */
  leftIcon?: React.ReactNode
  /**
   * Icon to display after text
   */
  rightIcon?: React.ReactNode
  /**
   * Dismissible badge
   */
  dismissible?: boolean
  /**
   * On dismiss callback
   */
  onDismiss?: () => void
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'default',
      size = 'md',
      shape = 'rounded',
      children,
      leftIcon,
      rightIcon,
      dismissible = false,
      onDismiss,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'font-medium',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-1'
    ]

    const variantClasses = {
      default: [
        'bg-gray-100',
        'text-gray-800',
        'hover:bg-gray-200'
      ],
      primary: [
        'bg-primary-100',
        'text-primary-800',
        'hover:bg-primary-200'
      ],
      secondary: [
        'bg-gray-100',
        'text-gray-800',
        'hover:bg-gray-200'
      ],
      success: [
        'bg-green-100',
        'text-green-800',
        'hover:bg-green-200'
      ],
      warning: [
        'bg-yellow-100',
        'text-yellow-800',
        'hover:bg-yellow-200'
      ],
      danger: [
        'bg-red-100',
        'text-red-800',
        'hover:bg-red-200'
      ],
      info: [
        'bg-blue-100',
        'text-blue-800',
        'hover:bg-blue-200'
      ]
    }

    const sizeClasses = {
      sm: ['px-2', 'py-0.5', 'text-xs', 'gap-1'],
      md: ['px-2.5', 'py-1', 'text-sm', 'gap-1.5'],
      lg: ['px-3', 'py-1.5', 'text-base', 'gap-2']
    }

    const shapeClasses = {
      rounded: ['rounded-md'],
      pill: ['rounded-full'],
      square: ['rounded-none']
    }

    const interactiveClasses = dismissible ? [
      'cursor-pointer',
      'hover:scale-105',
      'active:scale-95'
    ] : []

    const badgeClasses = [
      ...baseClasses,
      ...variantClasses[variant],
      ...sizeClasses[size],
      ...shapeClasses[shape],
      ...interactiveClasses,
      className
    ].join(' ')

    const handleDismiss = (e: React.MouseEvent) => {
      e.stopPropagation()
      onDismiss?.()
    }

    return (
      <span
        ref={ref}
        className={badgeClasses}
        {...props}
      >
        {leftIcon && (
          <span className="flex-shrink-0">{leftIcon}</span>
        )}
        
        <span>{children}</span>
        
        {rightIcon && !dismissible && (
          <span className="flex-shrink-0">{rightIcon}</span>
        )}
        
        {dismissible && (
          <button
            type="button"
            className="flex-shrink-0 ml-1 hover:bg-black hover:bg-opacity-10 rounded-full p-0.5 transition-colors"
            onClick={handleDismiss}
            aria-label="Remove badge"
          >
            <svg
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export { Badge }
